import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Divider,
  Typography,
  Stack,
  MenuItem,
  IconButton,
  Popover,
} from "@mui/material";
import account from "../../../_mock/account";
import { FILE_BASE_URL } from "../../../constants";
import Iconify from "../../../components/Iconify";
import { sentenceCase } from "change-case";
import { AppUser } from "../../../pages/api";
import { Access, App, Role } from "../../../models";
import Avatar from "../../../components/Avatar";
import { useLogoutMutation } from "../../../pages/login/loginSlice";
import { setMessage } from "../../../store/messageReducer";
import { toErrorMsg } from "../../../utils";

export interface IAccountPopover {
  appUser: AppUser;
}

export default function AccountPopover({ appUser }: IAccountPopover) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(null);
  // const appUser = useSelector((state: RootState) => state.appUser);

  const [logout] = useLogoutMutation();

  const MENU_OPTIONS = useMemo(
    () => [
      {
        label: t("Dashboard"),
        icon: "eva:home-fill",
        callback: () => {
          navigate("/dashboard");
        },
      },
      {
        label: t("Profile"),
        icon: "eva:person-fill",
        callback: () => {
          navigate("/profile");
        },
      },
      ...(appUser?.user?.role === Role.SUPERADMIN ||
      appUser?.user?.role === Role.ADMIN
        ? [
            {
              label: t("Settings"),
              icon: "eva:settings-2-fill",
              callback: () => {
                navigate("/settings");
              },
            },
            {
              label: t("Wallet"),
              icon: "mdi:wallet-outline",
              callback: () => {
                navigate("/wallet");
              },
            },

            ...(appUser.user?.currentClient?.isBranch &&
            (appUser?.user?.access?.includes(Access.RESTAURANT) ||
              // appUser?.user?.access?.includes(Access.OUTDOOR_ORDER) ||
              appUser?.user?.access?.includes(Access.SALE) ||
              appUser?.user?.access?.includes(Access.ORDER) ||
              appUser?.user?.access?.includes(Access.INVOICE) ||
              appUser?.user?.access?.includes(Access.QUOTATION)) &&
            (appUser?.user?.role === Role.SUPERADMIN ||
              appUser?.user?.role === Role.ADMIN ||
              appUser?.user?.role === Role.MANAGER)
              ? [
                  {
                    label: t("Printers"),
                    icon: "solar:printer-bold",
                    callback: () => {
                      navigate("/printers");
                    },
                  },
                ]
              : []),

            ...(process.env.REACT_APP_APP !== App.APP
              ? [
                  {
                    label: t("Pricing"),
                    icon: "solar:tag-price-bold",
                    callback: () => {
                      navigate("/pricing");
                    },
                  },
                ]
              : []),
          ]
        : []),
    ],
    [appUser],
  );

  const handleOpen = (event: any) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const onLogout = async () => {
    try {
      const { success, error, invalidData }: any = await logout(null).unwrap();
      if (success) {
        window.location.href = "/";
      }
      if (error) {
        dispatch(
          setMessage({
            type: "error",
            message: toErrorMsg(error),
          }),
        );
      }
      if (invalidData) {
        dispatch(
          setMessage({
            type: "error",
            message: t("Pleasecorrecterrors2"),
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        setMessage({
          type: "error",
          message: error.message,
        }),
      );
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open
            ? {
                "&:before": {
                  zIndex: 1,
                  content: "''",
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  position: "absolute",
                  bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
                },
              }
            : {}),
        }}
        size="small"
      >
        <Avatar
          file={appUser.user?.picture || account.photoURL}
          alt={appUser.user?.name}
          baseUrl={appUser.user?.picture ? FILE_BASE_URL.PROFILE_PICTURE : ""}
          pointer={true}
        />
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 0,
            mt: 1.5,
            ml: 0.75,
            width: 180,
            "& .MuiMenuItem-root": {
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        {appUser?.user && (
          <Box sx={{ my: 1.5, px: 2.5 }}>
            <Typography variant="subtitle2" noWrap>
              {appUser.user?.name}
            </Typography>
            {appUser.user?.email && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary" }}
                noWrap
              >
                {appUser.user?.email}
              </Typography>
            )}
            {appUser.user?.role && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary" }}
                noWrap
              >
                {t(sentenceCase(appUser.user?.role))}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ borderStyle: "dashed" }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={option.callback}>
              <Iconify
                icon={option.icon}
                width={21}
                height={21}
                style={{ marginRight: "10px", color: "#637381" }}
              />
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: "dashed" }} />

        <MenuItem onClick={onLogout} sx={{ m: 1, color: "#FF0000" }}>
          <Iconify
            icon="hugeicons:logout-04"
            width={21}
            height={21}
            style={{ marginRight: "10px", color: "#FF0000" }}
          />
          {t("Logout")}
        </MenuItem>
      </Popover>
    </>
  );
}
